import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { CartProvider } from './context/CartContext';
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingCheckoutBar from './components/FloatingCheckoutBar';
import Home from './pages/Home';
import Menu from './pages/Menu';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyOTP from './pages/VerifyOTP';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PaymentStatus from './pages/PaymentStatus';
import Orders from './pages/Orders';
import Profile from './pages/Profile';
import RoleRoute from './components/RoleRoute';
import AdminLayout from './pages/admin/AdminLayout';
import DashboardOverview from './pages/admin/DashboardOverview';
import ManageMenu from './pages/admin/ManageMenu';
import ManageOrders from './pages/admin/ManageOrders';
import ManageUsers from './pages/admin/ManageUsers';
import PaymentSettings from './pages/admin/PaymentSettings';
import StoreSettings from './pages/admin/StoreSettings';
import SubscriptionSettings from './pages/admin/SubscriptionSettings';
import HomepageEditor from './pages/admin/HomepageEditor';
import POSBilling from './pages/admin/POSBilling';
import ManageCoupons from './pages/admin/ManageCoupons';
import PayrollManagement from './pages/admin/PayrollManagement';
import ManageCampaigns from './pages/admin/ManageCampaigns';
import FranchiseBranches from './pages/admin/FranchiseBranches';
import FranchiseTransactions from './pages/admin/FranchiseTransactions';
import FranchiseBranding from './pages/admin/FranchiseBranding';

// Social Media Marketing
import SocialMediaDashboard from './pages/admin/social-media/SocialMediaDashboard';
import SocialMediaConnect from './pages/admin/social-media/SocialMediaConnect';
import SocialMediaComposer from './pages/admin/social-media/SocialMediaComposer';
import SocialMediaCalendar from './pages/admin/social-media/SocialMediaCalendar';
import SocialMediaAnalytics from './pages/admin/social-media/SocialMediaAnalytics';
import SocialMediaComments from './pages/admin/social-media/SocialMediaComments';
import SocialMediaTemplates from './pages/admin/social-media/SocialMediaTemplates';

// Table Reservations
import ReservationsList from './pages/admin/reservations/ReservationsList';
import ReservationSettings from './pages/admin/reservations/ReservationSettings';
import KitchenDashboard from './pages/kitchen/KitchenDashboard';
import DeliveryDashboard from './pages/delivery/DeliveryDashboard';
import Unauthorized from './pages/Unauthorized';
import Loading from './components/Loading';

import { useTenant } from './context/TenantContext';
import Portal from './pages/Portal';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import AdminTemplateEditor from './pages/admin/AdminTemplateEditor';
import FranchiseOnboard from './pages/FranchiseOnboard';
import SetPassword from './pages/SetPassword';

// Aaharam Sites - Static Website Builder
import SitesLogin from './pages/sites/SitesLogin';
import SitesDashboard from './pages/sites/SitesDashboard';
import SiteEditor from './pages/sites/SiteEditor';
import SiteTemplates from './pages/sites/SiteTemplates';
import SiteSettings from './pages/sites/SiteSettings';
import SiteSubscription from './pages/sites/SiteSubscription';

function AppContent() {
    const { loading } = useAuth();
    const { isPortal, tenant } = useTenant();

    useEffect(() => {
        if ('Notification' in window && 'serviceWorker' in navigator) {
            if (Notification.permission === 'default') {
                Notification.requestPermission().then(permission => {
                    console.log('[PWA] Notification permission status:', permission);
                });
            }
        }
    }, []);

    // For the Portal (Landing Page), we don't need to block everything for Auth check.
    // This makes the landing page feel instantaneous.
    if (loading && !isPortal) {
        return <Loading mode="full" message="Preparing your table..." />;
    }

    if (isPortal) {
        return (
            <Routes>
                <Route path="/" element={<Portal />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-otp" element={<VerifyOTP />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/franchise-onboard" element={<FranchiseOnboard />} />
                <Route path="/set-password" element={<SetPassword />} />
                {/* Aaharam Sites - Static Website Builder */}
                <Route path="/sites/login" element={<SitesLogin />} />
                <Route path="/sites/dashboard" element={<SitesDashboard />} />
                <Route path="/sites/editor/:id" element={<SiteEditor />} />
                <Route path="/sites/templates" element={<SiteTemplates />} />
                <Route path="/sites/:id/settings" element={<SiteSettings />} />
                <Route path="/sites/subscription" element={<SiteSubscription />} />
                <Route element={<RoleRoute allowedRoles={['super_admin']} />}>
                    <Route path="/super-admin" element={<SuperAdminDashboard />} />
                    <Route path="/super-admin/templates/create" element={<AdminTemplateEditor />} />
                    <Route path="/super-admin/templates/editor/:id" element={<AdminTemplateEditor />} />
                </Route>
                {/* Fallback to portal home */}
                <Route path="*" element={<Portal />} />
            </Routes>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {!isPortal && tenant?.use_test_payment && (
                <div className="bg-primary-600 text-white px-4 py-2 text-center text-xs font-bold tracking-widest uppercase shadow-sm border-b border-primary-700">
                    Site is in Test Payment Mode - Orders will not be charged
                </div>
            )}
            <Navbar />
            <Routes>
                {/* Public / Customer Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/branch/:slug" element={<Home />} />
                <Route path="/menu" element={<Menu />} />
                <Route path="/payment-status" element={<PaymentStatus />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-otp" element={<VerifyOTP />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/set-password" element={<SetPassword />} />
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Restricted Customer Routes */}
                <Route element={<RoleRoute allowedRoles={['customer']} />}>
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/orders" element={<Orders />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['customer', 'admin', 'restaurant_admin', 'franchise_admin', 'kitchen_staff', 'delivery_partner']} />}>
                    <Route path="/profile" element={<Profile />} />
                </Route>

                {/* Admin Routes */}
                <Route element={<RoleRoute allowedRoles={['admin', 'restaurant_admin', 'franchise_admin']} />}>
                    <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<DashboardOverview />} />
                        <Route path="pos" element={<POSBilling />} />
                        <Route path="menu" element={<ManageMenu />} />
                        <Route path="orders" element={<ManageOrders />} />
                        <Route path="coupons" element={<ManageCoupons />} />
                        <Route path="users" element={<ManageUsers />} />
                        <Route path="payroll" element={<PayrollManagement />} />
                        <Route path="homepage" element={<HomepageEditor />} />
                        <Route path="payment-settings" element={<PaymentSettings />} />
                        <Route path="settings" element={<StoreSettings />} />
                        <Route path="subscription" element={<SubscriptionSettings />} />
                        <Route path="campaigns" element={<ManageCampaigns />} />
                        <Route path="branches" element={<FranchiseBranches />} />
                        <Route path="transactions" element={<FranchiseTransactions />} />
                        <Route path="branding" element={<HomepageEditor />} />
                        {/* Social Media Marketing */}
                        <Route path="social-media" element={<SocialMediaDashboard />} />
                        <Route path="social-media/connect" element={<SocialMediaConnect />} />
                        <Route path="social-media/compose" element={<SocialMediaComposer />} />
                        <Route path="social-media/calendar" element={<SocialMediaCalendar />} />
                        <Route path="social-media/analytics" element={<SocialMediaAnalytics />} />
                        <Route path="social-media/comments" element={<SocialMediaComments />} />
                        <Route path="social-media/templates" element={<SocialMediaTemplates />} />
                        {/* Table Reservations */}
                        <Route path="reservations" element={<ReservationsList />} />
                        <Route path="reservations/settings" element={<ReservationSettings />} />
                    </Route>
                </Route>

                {/* Kitchen Routes */}
                <Route element={<RoleRoute allowedRoles={['kitchen_staff', 'admin', 'restaurant_admin', 'super_admin']} />}>
                    <Route path="/kitchen" element={<KitchenDashboard />} />
                </Route>

                {/* Delivery Routes */}
                <Route element={<RoleRoute allowedRoles={['delivery_partner', 'admin', 'restaurant_admin', 'super_admin']} />}>
                    <Route path="/delivery" element={<DeliveryDashboard />} />
                </Route>
            </Routes>
            <Footer />
            <FloatingCheckoutBar />
        </div>
    );
}

import { TenantProvider } from './context/TenantContext';
import { SiteAuthProvider } from './context/SiteAuthContext';

function App() {
    return (
        <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
            <Router>
                <TenantProvider>
                    <AuthProvider>
                        <SiteAuthProvider>
                            <NotificationProvider>
                                <CartProvider>
                                    <ToastProvider>
                                        <AppContent />
                                    </ToastProvider>
                                </CartProvider>
                            </NotificationProvider>
                        </SiteAuthProvider>
                    </AuthProvider>
                </TenantProvider>
            </Router>
        </GoogleOAuthProvider>
    );
}

export default App;